import {
  BehaviorSubject,
  combineLatest,
  Observable,
  ObservableInput,
  of,
  OperatorFunction,
  pipe,
  UnaryFunction,
} from 'rxjs';
import {
  concatMap,
  delay,
  expand,
  filter,
  map,
  scan,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { lift, LiftedEvent, replayLast } from '../rx/util';

export interface Loadable<T> {
  loading: boolean;
  erred: boolean;
  data: T | null;
  error?: any;
}

export namespace Loadable {
  export function data<T>(data: T): Loadable<T> {
    return { loading: false, erred: false, data };
  }
}

export function emptyLoadable<T>() {
  return new BehaviorSubject<Loadable<T>>({
    loading: false,
    erred: false,
    data: null,
  });
}

export function defaultLoadable<T>(data: T) {
  return new BehaviorSubject<Loadable<T>>({
    loading: false,
    erred: false,
    data,
  });
}

export function mapLoadable<A, B>(
  loadable: Loadable<A>,
  f: (value: A) => B,
): Loadable<B> {
  return loadable.data != null
    ? { ...loadable, data: f(loadable.data) }
    : <Loadable<any>>loadable;
}

export function load<T>(
  state: BehaviorSubject<Loadable<T>>,
  obs: Observable<T>,
) {
  state.next({
    loading: true,
    erred: false,
    data: state.value.data,
  });
  obs.subscribe({
    next: (val: T) => {
      state.next({
        loading: false,
        erred: false,
        data: val,
      });
    },
    error: error => {
      state.next({
        loading: false,
        erred: true,
        data: state.value.data,
        error,
      });
    },
  });
}

export function loadWith<T>(state: BehaviorSubject<Loadable<T>>, value: T) {
  load(state, of(value));
}

export function onDataChange<T>(
  state: BehaviorSubject<Loadable<T>>,
  callback: (value: T) => void,
) {
  state
    .pipe(
      filter(v => v.data !== null),
      map(v => v.data!),
    )
    .subscribe(callback);
}

export function invalidate<T>(state: BehaviorSubject<Loadable<T>>) {
  state.next({
    ...state.getValue(),
    loading: true,
  });
}

export function merge<A>(a: Loadable<A>): Loadable<[A]>;
export function merge<A, B>(a: Loadable<A>, b: Loadable<B>): Loadable<[A, B]>;
export function merge<A, B, C>(
  a: Loadable<A>,
  b: Loadable<B>,
  c: Loadable<C>,
): Loadable<[A, B, C]>;
export function merge<T>(...loadables: Loadable<T>[]): Loadable<T[]>;
export function merge<T>(...loadables: Loadable<T>[]): Loadable<T[]> {
  return {
    data: loadables.every(({ data }) => data !== null)
      ? loadables.map(({ data }) => data!)
      : null,
    loading: loadables.some(({ loading }) => loading),
    erred: loadables.some(({ erred }) => erred),
    error: loadables.filter(({ error }) => error)[0]?.error,
  };
}

export function mergePipe<A>(): OperatorFunction<Loadable<A>[], Loadable<A[]>> {
  return pipe(map(loadables => merge(...loadables)));
}

/**
 * Consolidate the number of loaders for pages with several.
 */
export function allLoading(
  ...observables: Observable<Loadable<any>>[]
): Observable<boolean> {
  return combineLatest(observables).pipe(
    scan<Loadable<any>[], boolean>(
      (loading, loadables) =>
        loading
          ? !loadables.some(({ loading }) => loading)
          : loadables.every(({ loading }) => loading),
      false,
    ),
  );
}

/**
 * Given Events, produce an Observable of Loadables
 *
 * @param allowMultiple By default, use historical behavior where first emission is counted as loaded (RIV-1496).
 */
function liftedToLoadable<T>(
  allowMultiple: boolean = false,
): OperatorFunction<LiftedEvent<T>, Loadable<T>> {
  return pipe(
    scan<LiftedEvent<T>, Loadable<T>>(
      (load, event) => {
        switch (event.type) {
          case 'complete':
            return { ...load, loading: false };
          case 'error':
            return { ...load, erred: true, loading: false, error: event.error };
          case 'next':
            return { ...load, data: event.value, loading: allowMultiple };
          case 'start':
            return { ...load, erred: false, loading: true, error: undefined };
        }
      },
      {
        loading: false,
        erred: false,
        data: null,
      },
    ),
  );
}

/**
 * Given an Observable of values and a loading function, produce an Obseravable
 * of Loadables.
 */
export function concatLoadMap<A, B>(
  load: (value: A) => ObservableInput<B>,
  allowMultiple: boolean = false,
): OperatorFunction<A, Loadable<B>> {
  return pipe(
    concatMap(value =>
      of(value).pipe(
        switchMap(load), // wrap load in case it throws
        lift(),
      ),
    ),
    liftedToLoadable(allowMultiple),
    startWith({ data: null, erred: false, loading: false }),
    replayLast(),
  );
}

/**
 * Given an Observable of values and a loading function, produce an Observable
 * of Loadables.
 */
export function loadMap<A, B>(
  load: (value: A) => ObservableInput<B>,
  allowMultiple: boolean = false,
): OperatorFunction<A, Loadable<B>> {
  return pipe(
    switchMap(value =>
      of(value).pipe(
        switchMap(load), // wrap load in case it throws
        lift(),
      ),
    ),
    tap(event => {
      if (event.type === 'error') {
        console.error(event.error); // eslint-disable-line no-console
      }
    }),
    liftedToLoadable(allowMultiple),
    startWith(<Loadable<B>>{ data: null, erred: false, loading: false }),
    replayLast(),
  );
}

export function loadPipe<A>(
  allowMultiple: boolean = false,
): OperatorFunction<A, Loadable<A>> {
  return pipe(
    lift(),
    liftedToLoadable(allowMultiple),
    startWith<Loadable<A>>({ data: null, erred: false, loading: false }),
    replayLast(),
  );
}

// success - assumes a return payload
export function success<T>(): UnaryFunction<
  Observable<Loadable<T>>,
  Observable<T>
> {
  return pipe(
    filter(({ data, loading, erred }) => !!data && !loading && !erred),
    map(({ data }) => data!),
  );
}

// successful completion - does not depend on return payload
export function complete<T>(): UnaryFunction<
  Observable<Loadable<T>>,
  Observable<T | null>
> {
  return pipe(
    scan(
      (accum, { data, loading, erred }) => {
        let wasLoading = false;
        if (accum.loading && !loading) {
          wasLoading = true;
        }
        accum = {
          data,
          loading,
          erred,
          wasLoading,
        };
        return accum;
      },
      { data: <T | null>null, wasLoading: false, erred: false, loading: false },
    ),
    filter(
      ({ wasLoading, loading, erred }) => wasLoading && !loading && !erred,
    ),
    map(({ data }) => data),
  );
}

export function erred<T>(): UnaryFunction<
  Observable<Loadable<T>>,
  Observable<Loadable<T>['error']>
> {
  return pipe(
    filter(({ loading, erred }) => !loading && erred),
    map(({ error }) => error),
  );
}

export function result<T>(): UnaryFunction<
  Observable<Loadable<T>>,
  Observable<T>
> {
  return pipe(
    filter(({ loading }) => !loading),
    map(({ erred, data }) => {
      if (erred) {
        throw new Error();
      }
      return data!;
    }),
  );
}

export function poll<T>(
  interval: number,
  f: () => Observable<T>,
): Observable<T> {
  return f().pipe(
    expand(() => of(undefined).pipe(delay(interval), switchMap(f)), 1),
  );
}
